'use client';

import { initializeApp, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { firebaseConfig } from './config';
import React from 'react';

// --- Singleton Pattern with Named App ---

const UNIQUE_APP_NAME = 'trade-insights-app';

// Module-level cache for our uniquely named Firebase app and its services.
let appInstance: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let firestoreInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;

/**
 * Initializes and/or retrieves a uniquely named Firebase app instance.
 * This function is idempotent and safe to call multiple times. It completely
 * bypasses the default '[DEFAULT]' app instance that may be injected by the
 * environment, ensuring we always use our specific configuration.
 */
export function initializeFirebase(): {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
} {
  if (!appInstance) {
    try {
      // Try to get the app if it was already initialized elsewhere with the same name.
      appInstance = getApp(UNIQUE_APP_NAME);
    } catch (e) {
      // If it doesn't exist, initialize it with our specific config and unique name.
      appInstance = initializeApp(firebaseConfig, UNIQUE_APP_NAME);
    }
    
    // Derive services from our named app instance.
    authInstance = getAuth(appInstance);
    firestoreInstance = getFirestore(appInstance);
    storageInstance = getStorage(appInstance);
  }

  // Always return the cached, correctly-configured instances.
  return { app: appInstance, auth: authInstance!, firestore: firestoreInstance!, storage: storageInstance! };
}

// Export initialized auth instance for direct import
export const { auth } = initializeFirebase();


// --- Exports for App Compatibility ---
// The rest of the exports remain to ensure compatibility with existing hooks and providers.

import {
  useFirebase,
  useFirebaseApp,
  useAuth,
  useFirestore
} from './provider';

import { FirebaseClientProvider } from './client-provider';
import { useCollection } from './firestore/use-collection';
import { useDoc } from './firestore/use-doc';
import { useUser } from './auth/use-user';

// This provider is a pass-through. All logic is now in FirebaseClientProvider
// which uses the guaranteed singleton from initializeFirebase().
const FirebaseProvider = ({ children }: { children: React.ReactNode }) => {
    return <>{children}</>;
}


export {
  FirebaseProvider,
  FirebaseClientProvider,
  useCollection,
  useDoc,
  useUser,
  useFirebase,
  useFirebaseApp,
  useAuth,
  useFirestore,
};
